/**
 * ECAPA-TDNN in the browser, via onnxruntime-web.
 *
 * The exported graph takes **raw 16 kHz float32 samples** and returns an
 * L2-normalised 192-dim embedding. Feature extraction lives inside the graph on
 * purpose: if the browser computed log-mel features itself, any disagreement with
 * SpeechBrain's Fbank -- window function, mel convention, log offset, dB scaling --
 * would produce a plausible-looking embedding that matches nobody, with no error
 * raised. `tools/export_ecapa_onnx.py --verify` asserts the graph reproduces
 * SpeechBrain to cosine 1.000000 on real clips before it ships.
 *
 * The model is ~84 MB. int8 quantisation would cut it to ~22 MB and was measured:
 * cosine agreement dropped to 0.959-0.991. That is not safe here, because one
 * participant on a quantised model and another on fp32 would have systematically
 * offset confidences, and the merge decides overlap by comparing them.
 */

/**
 * Root-absolute, not resolved from `import.meta.url`.
 *
 * This file is bundled now, so `import.meta.url` is the hashed chunk's URL under
 * `/assets/` and `"../models/ecapa.onnx"` would resolve against that — a different URL on
 * every deploy. It has to be the same URL on every deploy for a reason that has nothing
 * to do with tidiness: this string is a **cache key**. `sw.js` holds the 84 MB download in
 * `meetfolks-assets-v1`, and a device that already has it gets a cache hit only for the
 * exact URL it stored. Changing this re-downloads 84 MB on every device in the field.
 *
 * Both deployments serve the model at the root — the Cloudflare Worker streams it from R2,
 * the Python server from `web/`'s build output — so root-absolute is not an assumption
 * about where the app is mounted, it is where the model is.
 */
const MODEL_URL = "/models/ecapa.onnx";

/**
 * Absolute, and it has to be. Root-absolute now, for the reason above — `/vendor/*` is
 * cached under the same `meetfolks-assets-v1` key as the model, and the 12 MB wasm runtime
 * is not worth re-downloading either.
 *
 * ORT reaches its wasm glue with a bare `import(wasmPaths + "ort-wasm-simd-threaded.mjs")`
 * from inside `vendor/ort.wasm.min.js`, which is loaded as a *classic* script — so a
 * relative specifier resolves against the script's own URL, not the document's.
 * `"./vendor/"` therefore became `/vendor/vendor/...`, a 404 that surfaces as the
 * unhelpful "no available backend found. ERR: [wasm] TypeError: importing a module
 * script failed", after the model has already downloaded.
 *
 * ORT does try to absolutise the prefix first, but with `new URL(file, prefix)` — and this
 * prefix is not a valid base, so that throws and it silently falls back to string
 * concatenation. That is fine here and was not fine before: concatenation yields
 * `/vendor/ort-wasm-simd-threaded.mjs`, which is root-absolute and so resolves against the
 * origin whichever base the browser picks. A leading-dot prefix is the form that cannot
 * survive that fallback. Same string feeds `locateFile` for the .wasm binary.
 */
const WASM_DIR = "/vendor/";

/**
 * The service worker's cache for the model and the wasm runtime, and what goes in it.
 *
 * `ASSET_CACHE` is `ASSET_VERSION` in `sw.js`, duplicated because a service worker cannot
 * import this module; `ecapa-fetch.test.ts` fails when the two disagree. The page writes the
 * model here itself only after a resumed download (#643): the ranged requests that finished
 * it bypass the service worker's cache, so without this write nothing complete would be
 * stored and the next boot would download 84 MB again.
 */
const ASSET_CACHE = "meetfolks-assets-v1";
const ASSET_CACHE_PREFIX = "meetfolks-assets-";
const HEAVY_URLS = [MODEL_URL, `${WASM_DIR}ort-wasm-simd-threaded.wasm`, `${WASM_DIR}ort-wasm-simd-threaded.mjs`];

/**
 * How a download survives a bad connection (#643).
 *
 * [V] A tunnel reconnect or a backgrounded tab ends the 84 MB response early or errors its
 * stream; before this, either one failed voice setup outright, and a stream that simply
 * stopped sending left `reader.read()` pending forever -- "Getting ready" with no way out.
 * Each interruption resumes from the byte it reached with `Range: bytes=<n>-`. `tries`
 * counts attempts in a row that brought no new bytes, so a slow connection that keeps
 * making progress is never given up on, and a dead one fails in a few seconds.
 */
const DOWNLOAD = { stallMs: 20000, tries: 4, backoffMs: [500, 1500, 4000] };

/**
 * A liveness bound on creating the session on the main thread, the fallback below.
 *
 * Generous for the same reason as `PROXY_TIMEOUT_MS`. It cannot interrupt synchronous wasm,
 * which is why the model's length is checked before ORT ever sees it; it catches the
 * asynchronous hang, such as a runtime import that never settles.
 */
const CREATE_TIMEOUT_MS = 60000;

let session = null;
let loading = null;
//: Whether inference actually ended up off the main thread. Not the same as asking for
//: it -- `createInferenceSession` falls back -- and the fallback is silent, so
//: `threading()` reports this for the same reason it reports the thread count.
let proxied = false;

/**
 * How long to wait for a proxied session before giving up on the worker.
 *
 * [M] Session creation is 284 ms on an M3 Pro. This is not a performance bound, it is a
 * liveness bound: the failure being guarded is a proxy worker that loads and then never
 * answers, which would leave `load()` pending forever and the app parked on its boot
 * spinner with no route out but `?reset=app`. Generous enough that a slow device
 * optimising an 84 MB graph is never mistaken for a hang.
 *
 * [T] Not observed -- the documented ORT worker failure here throws (see WASM_DIR above).
 * This exists because a hang is the one variant that cannot report itself.
 */
const PROXY_TIMEOUT_MS = 20000;

/**
 * How many WASM threads to embed on.
 *
 * Threads need SharedArrayBuffer, which needs cross-origin isolation, which needs COOP
 * and COEP on this document -- served by `CrossOriginIsolation` in
 * `meetfolks/server/app.py`. Falling back to single-threaded is slower but always works,
 * and the fallback is silent, which is why `threading()` exists to report it.
 *
 * [M] One 2 s window on an M3 Pro in headless Chrome: 175 ms on 1 thread, 51.6 ms on 4.
 * A 20 s claim chunk goes 2436 ms -> 802 ms, duty cycle 12.2% -> 4.0%.
 *
 * [M] **iOS Safari does grant SharedArrayBuffer under COOP/COEP** -- the open question
 * when this was written. Measured across two real sessions on the same pair of handsets,
 * normalised by claimed speech: an iPhone on iOS 18.7 went 0.42 -> 0.18 s per
 * claimed-second, a 2.3x gain against 3.3x on desktop. An Android 10 / Chrome 151 handset
 * gained only 1.17x (1.99 -> 1.70) and is now the sole bottleneck at roughly 88% duty
 * cycle. [T] Why that phone gains so little is open, and the thread count it actually got
 * is not recoverable from the server logs -- `updateCapabilities` does not send it.
 */
export function plannedThreads() {
  if (!globalThis.crossOriginIsolated) return 1;
  return Math.min(4, navigator.hardwareConcurrency || 2);
}

/**
 * What embedding is running on, for the diagnostics panel.
 *
 * `threads` is a *plan* until the model is loaded and `active` goes true; before that
 * ORT's own `env.wasm.numThreads` still holds its default rather than our choice, so
 * reading it would report a number this app never uses.
 */
export function threading() {
  return {
    isolated: Boolean(globalThis.crossOriginIsolated),
    threads: session ? globalThis.ort?.env?.wasm?.numThreads ?? null : plannedThreads(),
    active: session !== null,
    cores: navigator.hardwareConcurrency ?? null,
    proxied,
  };
}

/** Load the runtime and model once. Returns a progress-reporting promise. */
export function load(onProgress) {
  if (session) return Promise.resolve(session);
  if (loading) return loading;

  loading = (async () => {
    const ort = globalThis.ort;
    if (!ort) throw new Error("onnxruntime-web failed to load (vendor/ort.wasm.min.js)");

    ort.env.wasm.wasmPaths = WASM_DIR;
    ort.env.wasm.numThreads = plannedThreads();
    ort.env.logLevel = "error";

    const { bytes, resumed } = await oneTabAtATime(() => download(MODEL_URL, onProgress));
    if (resumed) await storeModel(bytes);
    try {
      session = await createInferenceSession(ort, bytes, onProgress);
    } catch (err) {
      // The bytes were the advertised length, so this is a copy that is whole and still does
      // not load, or a runtime that does not. Either one, kept, fails every boot from now on
      // without touching the network -- [V] the shape of #643 on two phones. Dropped, the
      // next attempt fetches fresh copies.
      await evictAssets();
      throw err;
    }
    return session;
  })();
  // Not cached as a failure: a later call (the boot screen's retry) gets a fresh attempt.
  loading.catch(() => {
    loading = null;
  });
  return loading;
}

/**
 * How long one tab waits for another tab's model download before downloading anyway (#659).
 *
 * The lock is a courtesy, not a guarantee: two tabs on one device would otherwise pull 84 MB
 * each at the same time. Bounded because the other tab may be hidden and throttled, and a
 * load waiting on a tab nobody is looking at would be an endless "Getting ready".
 */
const DOWNLOAD_LOCK = "meetfolks-model-download";
const DOWNLOAD_LOCK_WAIT_MS = 30000;

/**
 * Run `fn` (the model download) while holding a cross-tab lock, when the browser has one.
 *
 * The tab that waited then reads the copy the first tab stored, through the service worker.
 * Without `navigator.locks`, or when the wait runs out, it simply downloads.
 */
async function oneTabAtATime(fn) {
  const locks = globalThis.navigator?.locks;
  if (!locks?.request) return fn();
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), DOWNLOAD_LOCK_WAIT_MS);
  let entered = false;
  try {
    return await locks.request(DOWNLOAD_LOCK, { signal: abort.signal }, () => {
      entered = true;
      clearTimeout(timer);
      return fn();
    });
  } catch (err) {
    // Only the wait is given up on; a download that failed inside the lock fails as itself.
    if (entered) throw err;
    return fn();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Whether a whole copy of the model is already in Cache Storage (#659).
 *
 * Decides two things the person sees: whether a background load on a metered connection
 * would cost data (a saved copy costs none), and whether "Getting ready" needs the one-time
 * size note. The service worker stores only a whole 200 (#643), so a hit is a whole copy.
 * Never throws: no Cache Storage reads as no copy.
 */
export async function isCached() {
  try {
    const keys = (await caches.keys()).filter((k) => k.startsWith(ASSET_CACHE_PREFIX));
    for (const key of keys) {
      const hit = await (await caches.open(key)).match(MODEL_URL, { ignoreSearch: true });
      if (hit && hit.status === 200) return true;
    }
  } catch {
    /* No Cache Storage: nothing saved. */
  }
  return false;
}

/**
 * Drop the cached model and wasm runtime, keeping the app shell and the voice profile.
 *
 * Used after a copy fails to load and by the boot screen's retry. Never throws: a browser
 * with no Cache Storage has nothing to drop.
 */
export async function evictAssets() {
  try {
    const keys = (await caches.keys()).filter((k) => k.startsWith(ASSET_CACHE_PREFIX));
    for (const key of keys) {
      const cache = await caches.open(key);
      await Promise.all(HEAVY_URLS.map((url) => cache.delete(url, { ignoreSearch: true })));
    }
  } catch {
    /* No Cache Storage (or it is locked): nothing stored, nothing to drop. */
  }
}

/** Best-effort: a write that fails (quota, no Cache Storage) costs a re-download, not a boot. */
async function storeModel(bytes) {
  try {
    const name = (await caches.keys()).find((k) => k.startsWith(ASSET_CACHE_PREFIX)) ?? ASSET_CACHE;
    const cache = await caches.open(name);
    // Awaited before `create`, which transfers the buffer to the proxy worker and detaches
    // it here. `new Response` copies the bytes, so this costs a second 84 MB briefly -- only
    // on the resumed path, which is the one that needs it.
    await cache.put(
      MODEL_URL,
      new Response(bytes, {
        status: 200,
        headers: { "Content-Type": "application/octet-stream", "Content-Length": String(bytes.byteLength) },
      })
    );
  } catch (err) {
    console.warn("voice setup could not be stored for next time", err);
  }
}

/**
 * Create the session in a worker if the browser allows it, on this thread if not.
 *
 * **This is the difference between a responsive app and a frozen one, and it is not a
 * throughput change.** `sess.run()` is synchronous WASM: the calling thread is gone for
 * its duration, and `embedMany` yields only once per *batch*, so the block is eight runs
 * long. [M] Desktop Chrome, 4 threads, one 20 s claim chunk: the longest gap between
 * fires of a 16 ms interval was **405 ms** on the main thread and **16.8 ms** proxied,
 * against a 17.1 ms idle floor -- and no gap exceeded 50 ms at all. Scoring the same
 * chunk took 465 ms against 459 ms, so this buys the whole of that for nothing.
 * [A] Scaled by measured per-window cost the block is ≲1.4 s on an iPhone and ≲13.6 s on
 * the slower Android, which is what "the buttons do not respond" was.
 *
 * [M] **One batch is the floor, not the ceiling.** `embedMany`'s `setTimeout(0)` between
 * batches is not a reliable scheduling point: once the timer queue is already overdue the
 * browser may take that continuation ahead of an interval that was due earlier, so
 * consecutive batches run back to back. Forcing the fallback below on a 14-window chunk
 * produced a *single* 823 ms gap covering the whole pass rather than one gap per batch.
 * That is also why shrinking the batch is a weaker mitigation than it looks.
 *
 * [M] Accuracy is untouched and provably so: the same input through the same graph gave a
 * bit-identical embedding checksum on both arms. Same WASM, same thread count, different
 * host thread.
 *
 * The fallback is the point of the function. ORT's proxy worker is
 * `vendor/ort.wasm.min.js` itself, re-entered as a same-origin classic worker, so
 * `require-corp` does not block it and there is no fourth vendor file for `sw.js` to
 * cache -- but it then has to spawn nested pthread workers under cross-origin isolation,
 * and [T] that is unverified on iOS Safari. This file already documents an ORT worker
 * failure that surfaced only *after* the 84 MB download completed (see `WASM_DIR`), which
 * is exactly the shape of bug that must not be allowed to leave the app with no backend
 * at all.
 */
async function createInferenceSession(ort, bytes, onProgress) {
  // `basic` optimization level avoids expensive and memory-duplicating graph cloning
  // in WebAssembly memory at boot on mobile devices (e.g. iOS Safari RangeError: Out of memory),
  // while retaining full CPU SIMD kernel performance on the constant-folded exported graph.
  const options = { executionProviders: ["wasm"], graphOptimizationLevel: "basic" };
  try {
    ort.env.wasm.proxy = true;
    const created = await Promise.race([
      ort.InferenceSession.create(bytes, options),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`proxy worker did not answer in ${PROXY_TIMEOUT_MS} ms`)), PROXY_TIMEOUT_MS)
      ),
    ]);
    proxied = true;
    return created;
  } catch (err) {
    // Not silent: `threading()` reports `proxied`, the diagnostics panel shows it, and
    // this line is the only trace on a device with no console.
    console.warn("scoring could not be moved off the main thread; the UI will block while scoring", err);
    ort.env.wasm.proxy = false;
    proxied = false;
    // ORT transfers the model buffer to the worker, which detaches it here. Re-read
    // rather than hand `create` an empty array -- the service worker holds `ecapa.onnx`
    // in its immutable cache, so this is a cache hit, not another 84 MB download.
    //
    // On the timeout path the abandoned attempt may still be holding a copy in a worker
    // that nothing can now reach. Accepted deliberately: a bounded boot is worth more
    // than the memory, and this path is not supposed to run.
    const fresh = bytes.byteLength ? bytes : await fetchWithProgress(MODEL_URL, onProgress);
    return await Promise.race([
      ort.InferenceSession.create(fresh, options),
      new Promise((_, reject) =>
        setTimeout(() => reject(new Error(`session did not load in ${CREATE_TIMEOUT_MS} ms`)), CREATE_TIMEOUT_MS)
      ),
    ]);
  }
}

/** The bytes at `url`, whole and checked against their advertised length. */
export async function fetchWithProgress(url, onProgress, options) {
  return (await download(url, onProgress, options)).bytes;
}

/** 4xx other than these will not change on a retry; 5xx, 408 and 429 might. */
const RETRYABLE_4XX = new Set([408, 429]);

class DownloadError extends Error {
  constructor(message, { permanent = false } = {}) {
    super(message);
    this.permanent = permanent;
  }
}

/**
 * Download `url` whole, resuming an interrupted response with Range requests.
 *
 * Returns the bytes and whether more than one response was needed to get them -- `load`
 * stores a resumed copy itself, because the service worker stores only a single whole 200.
 *
 * The length check is what keeps a partial copy away from ORT. [V] Without it a 10 MB prefix
 * of the 84 MB model, served from Cache Storage with the full Content-Length on it, reached
 * `InferenceSession.create` and failed every boot. Here that prefix is an early end of
 * stream like any other, and is finished from the network with a ranged request.
 *
 * With no usable length -- none sent, or a compressed body whose Content-Length is the
 * encoded size -- there is nothing to check against or resume from, so an interruption
 * restarts the download and ORT is the only judge of the bytes.
 */
async function download(url, onProgress, { stallMs, tries, backoffMs } = DOWNLOAD) {
  let out = null;
  let total = 0; // the full size, once a response has told us it
  let received = 0;
  let responses = 0;
  let idle = 0; // attempts in a row that brought no new bytes
  for (;;) {
    const before = received;
    let reason;
    try {
      if (await attempt()) {
        const bytes = out.byteLength === received ? out : out.slice(0, received);
        return { bytes, resumed: responses > 1 };
      }
      // Ended short of the length it promised: resume from the byte it reached.
      reason = `ended at ${received} of ${total} bytes`;
    } catch (err) {
      if (err instanceof DownloadError && err.permanent) throw new Error(`could not fetch ${url}: ${err.message}`);
      // Without a length there is no offset to resume from that the server could check.
      if (!total) restart();
      reason = err.message;
    }
    idle = received > before ? 0 : idle + 1;
    if (idle >= tries) throw new Error(`could not fetch ${url}: ${reason}`);
    // An attempt that brought nothing new waits before the next, so a reconnecting tunnel
    // has time to come back; one that made progress resumes straight away.
    if (idle > 0) await new Promise((r) => setTimeout(r, backoffMs[Math.min(idle, backoffMs.length) - 1]));
  }

  /** One response. Resolves true when the bytes are complete, false when it ended early. */
  async function attempt() {
    const abort = new AbortController();
    let timer;
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => abort.abort(new Error(`no data for ${stallMs} ms`)), stallMs);
    };
    arm();
    try {
      const res = await fetch(url, {
        signal: abort.signal,
        headers: received && total ? { Range: `bytes=${received}-` } : undefined,
      });
      responses += 1;
      start(res);
      if (!res.body) {
        // No stream to read incrementally (an old engine): take it whole.
        append(new Uint8Array(await res.arrayBuffer()));
        return finished();
      }
      const reader = res.body.getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) return finished();
        arm();
        append(value);
      }
    } finally {
      clearTimeout(timer);
      abort.abort();
    }
  }

  /** Check a response's status and range against what has been received so far. */
  function start(res) {
    if (res.status === 206) {
      const range = /^bytes (\d+)-(\d+)\/(\d+)$/.exec(res.headers.get("content-range") || "");
      const first = range ? Number(range[1]) : -1;
      const size = range ? Number(range[3]) : 0;
      // Appended only when it starts exactly where we stopped and describes the same file.
      // Anything else restarts from zero, since the bytes held may not belong with it.
      if (size > 0 && first === received && (received === 0 || size === total)) {
        if (received === 0) {
          // A 206 for the start of the file is as good as a 200 for it.
          total = size;
          out = new Uint8Array(total);
        }
        return;
      }
      const at = received;
      restart();
      throw new Error(`unexpected range ${res.headers.get("content-range")} at byte ${at}`);
    }
    if (!res.ok) {
      const permanent = res.status >= 400 && res.status < 500 && !RETRYABLE_4XX.has(res.status);
      throw new DownloadError(`${res.status}`, { permanent });
    }
    // A 200: the whole file from byte zero, whether that was asked for or the server ignored
    // the Range. Either way what is held so far is discarded.
    const encoded = (res.headers.get("content-encoding") || "identity") !== "identity";
    total = encoded ? 0 : Number(res.headers.get("content-length")) || 0;
    received = 0;
    out = new Uint8Array(total);
  }

  function restart() {
    received = 0;
    total = 0;
    out = null;
  }

  function append(chunk) {
    if (total && received + chunk.length > total) {
      const message = `more than ${total} bytes at byte ${received}`;
      restart();
      throw new Error(message);
    }
    if (received + chunk.length > out.length) {
      const larger = new Uint8Array(Math.max(received + chunk.length, out.length * 2));
      larger.set(out.subarray(0, received), 0);
      out = larger;
    }
    out.set(chunk, received);
    received += chunk.length;
    onProgress?.(total ? received / total : null);
  }

  function finished() {
    // No length to check against: whatever arrived is the file.
    if (!total) return true;
    return received === total;
  }
}

export function isLoaded() {
  return session !== null;
}

/** One waveform -> one L2-normalised Float32Array(192). */
export async function embed(wav) {
  const [only] = await embedMany([wav]);
  return only;
}

/**
 * Many equal-or-unequal-length waveforms -> array of embeddings.
 *
 * Windows are grouped by length so a batch needs no padding -- ECAPA's statistics
 * pooling would otherwise average silence into the vector.
 *
 * **Batching buys much less than this comment used to claim.** It said one-at-a-time was
 * "the difference between seconds and minutes on a phone"; [M] measured on desktop
 * Chrome it is tens of percent -- one 20 s chunk took 528 ms at batch 1 against 465 ms at
 * batch 8, and 569 against 459 on the proxied arm. The two arms disagreeing by nearly 2x,
 * on a 3-run median where batch *2* comes out slower per window than batch 1, is why
 * there is no single figure quoted here. What is solid is the order of magnitude: it is
 * not orders of magnitude.
 *
 * That matters because the batch is also the unit of main-thread blocking -- the yield
 * below happens once per batch, so a batch of 8 is eight uninterruptible `sess.run()`
 * calls. With `createInferenceSession` proxying to a worker that costs nothing, and
 * `batchSize = 1` is the fallback worth reaching for if a browser refuses the worker:
 * [M] it cuts the longest event-loop gap from 405 ms to 74 ms.
 */
export async function embedMany(waves, batchSize = 8, onProgress) {
  const sess = await load();
  const ort = globalThis.ort;
  const out = new Array(waves.length);

  const byLength = new Map();
  waves.forEach((w, i) => {
    if (!byLength.has(w.length)) byLength.set(w.length, []);
    byLength.get(w.length).push(i);
  });

  let done = 0;
  for (const [length, indices] of byLength) {
    for (let b = 0; b < indices.length; b += batchSize) {
      const group = indices.slice(b, b + batchSize);
      const flat = new Float32Array(group.length * length);
      group.forEach((idx, k) => flat.set(waves[idx], k * length));
      const tensor = new ort.Tensor("float32", flat, [group.length, length]);
      const result = await sess.run({ waveform: tensor });
      const data = result.embedding.data;
      const dim = result.embedding.dims[1];
      group.forEach((idx, k) => {
        out[idx] = new Float32Array(data.subarray(k * dim, (k + 1) * dim));
      });
      // Yield to the event loop so the UI stays responsive mid-session -- which is also
      // what makes the progress report below worth sending: the browser gets a chance to
      // paint it before the next batch starts.
      done += group.length;
      onProgress?.(done / waves.length);
      await new Promise((r) => setTimeout(r, 0));
    }
  }
  return out;
}

export function cosine(a, b) {
  let sum = 0;
  for (let i = 0; i < a.length; i++) sum += a[i] * b[i];
  return sum;
}

/** Mean of L2-normalised embeddings, renormalised. The template. */
export function centroid(vectors) {
  const dim = vectors[0].length;
  const mean = new Float32Array(dim);
  for (const v of vectors) for (let i = 0; i < dim; i++) mean[i] += v[i];
  let norm = 0;
  for (let i = 0; i < dim; i++) {
    mean[i] /= vectors.length;
    norm += mean[i] * mean[i];
  }
  norm = Math.sqrt(norm) + 1e-12;
  for (let i = 0; i < dim; i++) mean[i] /= norm;
  return mean;
}
